/**
 * ExplicitSaveTracking — lightweight context for tracking whether any
 * accordion-style action editor has unsaved changes, and for discarding
 * those changes when the user scrolls away or switches proposals.
 *
 * Accordion editors (CreateActionCard, UpdateActionCard, etc.) use explicit
 * Save/Cancel buttons rather than auto-saving via AutoSaveCoordination.
 * This context lets the inline scroll-away guard and edit-switch guard
 * know when there are unsaved changes that would be lost by scrolling
 * away or switching to another proposal.
 *
 * Auto-save editors (e.g. EmailActionPreview) do NOT
 * report here — their changes save automatically, so the guard should
 * not fire for them.
 *
 * Usage:
 *   - Provider: InlineProposalExpandedInner (wraps the proposal content)
 *   - Consumer: ActionAccordion (v2) — reports dirty state and cancel handler
 *   - Reader: InlineScrollAwayGuard (via hasDirtyChanges / discardAll)
 */

/* eslint-disable react-refresh/only-export-components -- standard context pattern */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ExplicitSaveTrackingValue {
  /**
   * Report whether an accordion editor has unsaved changes.
   * Called by ActionAccordion when isEditing/isDirty changes.
   * Cleanup: call with dirty=false on unmount.
   */
  reportDirty: (actionId: number, dirty: boolean) => void;
  /**
   * Register a cancel/discard callback for an accordion editor.
   * Called when the editor enters edit mode. Returns an unregister function.
   * The callback should reset the form and exit edit mode.
   */
  registerCancel: (actionId: number, cancelFn: () => void) => () => void;
  /**
   * Returns true if any accordion editor currently has unsaved changes.
   * Used by InlineScrollAwayGuard and hasPendingChanges handlers.
   */
  hasDirtyEdits: () => boolean;
  /**
   * Discard all unsaved accordion edits by calling every registered
   * cancel handler. Used by the Discard button in scroll-away and
   * edit-switch modals.
   */
  discardAll: () => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const ExplicitSaveTrackingContext =
  createContext<ExplicitSaveTrackingValue | null>(null);

// ─── Consumer hook ───────────────────────────────────────────────────────────

/**
 * Access the explicit save tracking context. Returns null when no provider
 * is present (e.g. in the sidebar panel which uses AutoSaveNavigationGuard).
 */
export function useExplicitSaveTracking(): ExplicitSaveTrackingValue | null {
  return useContext(ExplicitSaveTrackingContext);
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function ExplicitSaveTrackingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const dirtySetRef = useRef(new Set<number>());
  const cancelHandlersRef = useRef(new Map<number, () => void>());

  const reportDirty = useCallback((actionId: number, dirty: boolean) => {
    if (dirty) {
      dirtySetRef.current.add(actionId);
    } else {
      dirtySetRef.current.delete(actionId);
    }
  }, []);

  const registerCancel = useCallback(
    (actionId: number, cancelFn: () => void) => {
      cancelHandlersRef.current.set(actionId, cancelFn);
      return () => {
        cancelHandlersRef.current.delete(actionId);
      };
    },
    []
  );

  const hasDirtyEdits = useCallback(() => {
    return dirtySetRef.current.size > 0;
  }, []);

  const discardAll = useCallback(() => {
    cancelHandlersRef.current.forEach(cancelFn => {
      try {
        cancelFn();
      } catch {
        // Ignore errors from individual cancel handlers
      }
    });
    dirtySetRef.current.clear();
  }, []);

  const value = useMemo<ExplicitSaveTrackingValue>(
    () => ({ reportDirty, registerCancel, hasDirtyEdits, discardAll }),
    [reportDirty, registerCancel, hasDirtyEdits, discardAll]
  );

  return (
    <ExplicitSaveTrackingContext.Provider value={value}>
      {children}
    </ExplicitSaveTrackingContext.Provider>
  );
}
